/**
 * Preferencia de actualización automática, recordada por pantalla.
 *
 * Una por listado y no una por ruta: el de mensajes (`messages`), el de
 * corridas (`runs`) y la corrida (`run`), que comparten el detalle del mensaje, el flujo a pantalla
 * completa y la traza, porque son tres vistas de la misma corrida. Quien la
 * apaga para leer tranquilo un evento espera encontrarla apagada al abrir su
 * flujo.
 *
 * Se guarda en localStorage como comodidad, no como estado del que dependa
 * algo: si el navegador no deja leer ni escribir (modo privado, almacenamiento
 * bloqueado, cuota llena) la UI sigue funcionando con el valor en memoria.
 */
import { useCallback, useState } from 'react';

export type AutoRefreshScope = 'messages' | 'runs' | 'run';

export const AUTO_REFRESH_STORAGE_KEYS: Readonly<Record<AutoRefreshScope, string>> = {
  messages: 'middle:auto-refresh:messages',
  runs: 'middle:auto-refresh:runs',
  run: 'middle:auto-refresh:run',
};

/** Encendida mientras no se diga lo contrario: la pantalla muestra lo que pasa ahora. */
const DEFAULT_ENABLED = true;

const STORED_ON = 'on';
const STORED_OFF = 'off';

function readPreference(key: string): boolean {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(key);
  } catch {
    // Sin acceso al almacenamiento (modo privado, bloqueado por el navegador):
    // la preferencia es una comodidad y se usa el valor por defecto.
    return DEFAULT_ENABLED;
  }
  if (stored === STORED_OFF) {
    return false;
  }
  if (stored === STORED_ON) {
    return true;
  }
  // Nada guardado, o un valor que esta versión no conoce.
  return DEFAULT_ENABLED;
}

function writePreference(key: string, enabled: boolean): void {
  try {
    window.localStorage.setItem(key, enabled ? STORED_ON : STORED_OFF);
  } catch {
    // Sin almacenamiento la elección vale igual mientras la pantalla esté
    // abierta; lo único que se pierde es recordarla la próxima vez.
  }
}

/** `[encendida, cambiar]`, como `useState`, pero recordada entre visitas. */
export function useAutoRefreshPreference(
  scope: AutoRefreshScope,
): readonly [boolean, (enabled: boolean) => void] {
  const key = AUTO_REFRESH_STORAGE_KEYS[scope];
  const [enabled, setEnabledState] = useState(() => readPreference(key));

  const setEnabled = useCallback(
    (next: boolean): void => {
      setEnabledState(next);
      writePreference(key, next);
    },
    [key],
  );

  return [enabled, setEnabled] as const;
}
